import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../utils/api';
import {
  requestNotificationPermission,
  subscribeToPush,
  getExistingSubscription,
  fetchVapidPublicKey,
} from '../utils/pushNotifications';

interface User {
  id: number;
  username: string;
  email: string;
  avatar_url?: string;
  banner_url?: string;
  banner_offset_y?: number;
  bio?: string;
  favorite_color?: string;
  is_admin?: number | boolean;
  status?: string;
}

interface RegisterResult {
  pendingApproval: boolean;
  requiresEmailVerification: boolean;
  message?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, inviteCode?: string) => Promise<RegisterResult>;
  logout: () => void;
  isAuthenticated: boolean;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      // Token validieren, bevor wir ihn setzen
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (error) {
        // Ungültige User-Daten - entfernen
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } else {
      // Kein Token oder User - sicherstellen, dass State leer ist
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: newUser } = response.data;
    
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));

    // Push-Benachrichtigungen registrieren
    registerPushNotifications();
  };

  const registerPushNotifications = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return;
    }

    // VAPID Public Key vom Server abrufen
    await fetchVapidPublicKey();

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      return;
    }

    try {
      // Prüfe ob bereits eine Subscription existiert
      const existingSubscription = await getExistingSubscription();
      if (existingSubscription) {
        // Subscription an Server senden
        await api.post('/push/subscribe', {
          endpoint: existingSubscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(existingSubscription.getKey('p256dh')!),
            auth: arrayBufferToBase64(existingSubscription.getKey('auth')!),
          },
        });
        return;
      }

      // Neue Subscription erstellen
      const subscription = await subscribeToPush();
      if (subscription) {
        // Subscription an Server senden
        await api.post('/push/subscribe', {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')!),
            auth: arrayBufferToBase64(subscription.getKey('auth')!),
          },
        });
      }
    } catch (error) {
      console.error('Fehler bei Push-Benachrichtigungen:', error);
    }
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const register = async (username: string, email: string, password: string, inviteCode?: string) => {
    const response = await api.post('/auth/register', { username, email, password, invite_code: inviteCode });
    return {
      pendingApproval: !!response.data.pendingApproval,
      requiresEmailVerification: !!response.data.requiresEmailVerification,
      message: response.data.message,
    };
  };

  const logout = async () => {
    console.log('AuthContext logout aufgerufen');
    
    // Token und User sofort entfernen
    setToken(null);
    setUser(null);
    
    // Alle localStorage-Einträge entfernen
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Auch alle anderen möglichen Auth-bezogenen Einträge entfernen
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('auth_') || key.startsWith('token_') || key.startsWith('user_')) {
        localStorage.removeItem(key);
      }
    });
    
    // Push Subscription vom Server entfernen (versuchen, aber nicht blockieren)
    try {
      const subscription = await getExistingSubscription();
      if (subscription) {
        // Token temporär wiederherstellen für diesen einen Call
        const savedToken = localStorage.getItem('token');
        if (savedToken) {
          await api.post('/push/unsubscribe', {
            endpoint: subscription.endpoint,
          });
        }
      }
    } catch (error) {
      // Ignorieren, da wir bereits ausgeloggt sind
      console.log('Push-Unsubscribe übersprungen (Token bereits entfernt)');
    }
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => {
      if (!prev) {
        return prev;
      }
      const updatedUser = { ...prev, ...updates };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!token,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth muss innerhalb eines AuthProvider verwendet werden');
  }
  return context;
};

